/**
 * FaxMDOrderDialog - Fax MD Order Dialog Component
 * Extracted from ClientDetailPage
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Send } from "lucide-react";
import { DischargeOrderFields } from "./DischargeOrderFields";
import { ServiceHoldFields } from "./ServiceHoldFields";

export function FaxMDOrderDialog({
  open,
  onOpenChange,
  faxOrderTypes,
  setFaxOrderTypes,
  faxNotes,
  setFaxNotes,
  physician,
  sendingFax,
  onSendFax,
  discharge,
  setDischarge,
  hold,
  setHold,
  dme,
  setDme,
  client,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fax MD Order</DialogTitle>
          <DialogDescription>
            Select the type of orders to request from the physician.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Order</Label>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="personal_care"
                  checked={faxOrderTypes.personal_care}
                  onCheckedChange={(checked) =>
                    setFaxOrderTypes(prev => ({ ...prev, personal_care: checked }))
                  }
                />
                <Label htmlFor="personal_care" className="font-normal">
                  Personal Care Service Evaluation and Treatment and Last 3 MD visit notes
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="dme_supplies"
                  checked={faxOrderTypes.dme_supplies}
                  onCheckedChange={(checked) =>
                    setFaxOrderTypes(prev => ({ ...prev, dme_supplies: checked }))
                  }
                />
                <Label htmlFor="dme_supplies" className="font-normal">
                  DME Supplies Order
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="service_hold"
                  checked={faxOrderTypes.service_hold}
                  onCheckedChange={(checked) =>
                    setFaxOrderTypes(prev => ({ ...prev, service_hold: checked }))
                  }
                />
                <Label htmlFor="service_hold" className="font-normal">
                  Service Hold Order
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="discharge"
                  checked={faxOrderTypes.discharge}
                  onCheckedChange={(checked) =>
                    setFaxOrderTypes(prev => ({ ...prev, discharge: checked }))
                  }
                />
                <Label htmlFor="discharge" className="font-normal">
                  Discharge Order
                </Label>
              </div>
            </div>
          </div>

          {faxOrderTypes.dme_supplies && (
            <div className="space-y-3 p-4 bg-emerald-50 rounded-lg" data-testid="fax-dme-section">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-medium text-emerald-800">DME/Supplies Details</h4>
                {client?.dme_needed && dme?.dme_other !== client.dme_needed && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setDme((p) => ({ ...p, dme_other: client.dme_needed }))}
                    data-testid="fax-dme-use-needed-btn"
                  >
                    Use "DME Needed"
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fax-dme-items">DME Items/Supplies</Label>
                <Textarea
                  id="fax-dme-items"
                  value={dme?.dme_other || ""}
                  onChange={(e) => setDme((p) => ({ ...p, dme_other: e.target.value }))}
                  placeholder="List DME items and supplies needed..."
                  rows={3}
                  data-testid="fax-dme-items-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="fax-dme-height">Patient Height</Label>
                  <Input
                    id="fax-dme-height"
                    value={dme?.patient_height || ""}
                    onChange={(e) => setDme((p) => ({ ...p, patient_height: e.target.value }))}
                    placeholder={'5\'8"'}
                    data-testid="fax-dme-height-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fax-dme-weight">Patient Weight</Label>
                  <Input
                    id="fax-dme-weight"
                    value={dme?.patient_weight || ""}
                    onChange={(e) => setDme((p) => ({ ...p, patient_weight: e.target.value }))}
                    placeholder="165 lbs"
                    data-testid="fax-dme-weight-input"
                  />
                </div>
              </div>
            </div>
          )}

          {faxOrderTypes.service_hold && (
            <ServiceHoldFields idPrefix="fax-hold" value={hold} onChange={setHold} />
          )}

          {faxOrderTypes.discharge && (
            <DischargeOrderFields idPrefix="fax-discharge" value={discharge} onChange={setDischarge} />
          )}

          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs font-medium text-slate-500 mb-1">Fax To:</p>
            <p className="text-sm text-slate-700">
              {physician?.name || "No physician on file"}
            </p>
            <p className="text-sm text-slate-500">
              {physician?.fax || "No fax number"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fax-notes">Notes (Optional)</Label>
            <Textarea
              id="fax-notes"
              placeholder="Additional notes for the physician..."
              value={faxNotes}
              onChange={(e) => setFaxNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSendFax}
            disabled={sendingFax}
            className="gap-2"
          >
            <Send className="w-4 h-4" />
            {sendingFax ? "Sending..." : "Send Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
